import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, ExternalLink, Store } from 'lucide-react';
import { SHOPIFY_APP_URL } from '@/config/links';

const SHOPIFY_LOGO = `${process.env.PUBLIC_URL || ''}/logos/shopify.svg`;

const highlights = [
  'Auto-sync Shopify orders',
  'Book shipments with multiple couriers',
  'Print labels & track in real time',
];

const ShopifyIntegration = () => {
  return (
    <section
      id="shopify-integration"
      aria-labelledby="shopify-integration-heading"
      className="relative py-24 bg-white overflow-hidden"
    >
      {/* Signature top/bottom hairlines + floating orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 md:mb-16">
          <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">
            Shopify Integration
          </span>
          <h2
            id="shopify-integration-heading"
            className="text-4xl md:text-5xl font-bold mt-2 mb-4 text-gray-900"
          >
            Seamless Integration with{' '}
            <span className="text-blue-800">Shopify</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Install the Boxd Logistics app on your Shopify store to auto-import
            orders, book shipments with multiple couriers, print labels, and
            track deliveries — all from one dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Logo card — matches feature-card styling */}
          <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
            <a
              href={SHOPIFY_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View Boxd Logistics on the Shopify App Store"
              className="relative group block w-full max-w-sm"
            >
              <div className="relative overflow-hidden p-8 sm:p-10 bg-gradient-to-br from-white to-blue-50/30 rounded-2xl border border-gray-200 group-hover:border-blue-500 group-hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/40 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative z-10 flex flex-col items-center">
                  <img
                    src={SHOPIFY_LOGO}
                    alt="Shopify"
                    width={220}
                    height={250}
                    loading="lazy"
                    decoding="async"
                    className="w-28 sm:w-32 md:w-36 h-auto transition-transform duration-300 group-hover:scale-105 drop-shadow-sm"
                  />
                  <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-900 text-xs font-semibold uppercase tracking-wider">
                    <span
                      className="w-1.5 h-1.5 rounded-full bg-[#95BF47]"
                      aria-hidden="true"
                    />
                    Live on Shopify App Store
                  </div>
                  <p className="mt-3 text-sm text-gray-500">
                    Free to install · Built by Boxd Logistics
                  </p>
                </div>

                <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-blue-700 to-blue-800 group-hover:w-full transition-all duration-300 rounded-b-2xl" />
              </div>
            </a>
          </div>

          {/* Benefits + CTAs */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <ul className="space-y-4 mb-8">
              {highlights.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 justify-center lg:justify-start"
                >
                  <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center shadow-md">
                    <CheckCircle2 className="w-4 h-4 text-white" aria-hidden="true" />
                  </span>
                  <span className="text-base md:text-lg text-gray-800 font-medium leading-7">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center lg:justify-start">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white font-semibold px-6 shadow-lg shadow-blue-700/20"
              >
                <a
                  href={SHOPIFY_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Store className="w-4 h-4 mr-2" />
                  Install on Shopify
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>

              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-blue-800 text-blue-900 hover:bg-blue-50 font-semibold px-6"
              >
                <a
                  href={SHOPIFY_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on App Store
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopifyIntegration;
